import {
  Banner,
  Button,
  DrawerSection,
  ErrorState,
  KeyValueGrid,
  Skeleton,
} from '@shared/design-system/ui'
import { useAdminReserveSync } from '../api/admin-receiving-plan'
import { reserveFailureMessage, reserveSyncState } from '../model/reserve-sync'
import { planPercent, type UserReceivingPlan } from '../model/user-receiving-plan'
import * as styles from './receiving-plan.css'

export function ReserveSyncStatus({ userId, plan }: { userId: string; plan: UserReceivingPlan }) {
  const query = useAdminReserveSync(userId, plan)
  if (query.isPending) return <Skeleton height={120} />
  if (query.isError)
    return (
      <ErrorState
        title="Não foi possível confirmar a reserva aplicada"
        onRetry={() => void query.refetch()}
      />
    )
  const state = reserveSyncState(plan, query.data, query.confirmationTimedOut)
  const applied = query.data.reserve?.applied
  const titles = {
    unlinked: 'Reserva aguardando vínculo da conta',
    pending: 'Reserva aguardando aplicação',
    unconfirmed: 'Reserva sem confirmação',
    failed: 'A reserva configurada não foi aplicada',
    applied: 'Reserva aplicada',
  }
  return (
    <div
      className={`${styles.stack} ${styles.reserveSync}`}
      data-testid="receiving-plan-reserve-sync"
      aria-live="polite"
    >
      <DrawerSection title="Aplicação da reserva">
        <KeyValueGrid
          items={[
            {
              label: 'Última reserva aplicada na Stripe',
              value: applied
                ? `${planPercent(String(applied.percent))} por ${applied.daysAfterCharge} dias`
                : 'Nenhuma reserva aplicada',
            },
          ]}
        />
      </DrawerSection>
      <Banner
        tone={state === 'applied' ? 'success' : state === 'failed' ? 'danger' : 'info'}
        title={titles[state]}
      >
        {state === 'applied'
          ? 'As condições configuradas estão confirmadas na Stripe para novas vendas deste participante.'
          : state === 'unlinked'
            ? 'A reserva será enviada após o vínculo da conta. Salvar o plano não confirma sua aplicação.'
            : state === 'failed'
              ? `${reserveFailureMessage(plan, query.data)} Até a confirmação, permanece a última reserva aplicada; sem reserva inicial, o participante não fica apto a vender.`
              : state === 'unconfirmed'
                ? 'Não recebemos confirmação em 2 minutos. A consulta automática foi pausada. Atualize a situação ou acione o suporte. Permanece a última reserva aplicada; sem reserva inicial, o participante não fica apto a vender.'
                : 'Aguarde a confirmação da Stripe. Até lá, permanece a última reserva aplicada; sem reserva inicial, o participante não fica apto a vender.'}
      </Banner>
      <div className={styles.actions}>
        <Button
          variant="secondary"
          data-testid="receiving-plan-refresh-reserve-btn"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          {query.isFetching ? 'Atualizando...' : 'Atualizar situação da reserva'}
        </Button>
      </div>
    </div>
  )
}
